/**
 * Inspection Template Resolver
 * ----------------------------------------------------------------------------
 * Lightweight category-aware resolver for pre-hire / post-hire checklists.
 *
 * Design intent:
 *   SAME ENGINE + SAME STORAGE / EVIDENCE / AUDIT
 *   + DIFFERENT TEMPLATE DEFINITIONS PER CATEGORY
 *
 * `vehicle_default` is a sentinel — the existing multi-step `PreHireChecklistWizard`
 * (and its post-hire counterpart) remains the canonical implementation for every
 * vehicle-style category. When a listing's `vehicle_category === 'specialty_asset'`,
 * the resolver returns the specialty template and the booking detail page mounts
 * the specialty inspection wizard instead.
 *
 * Future categories (trailer-only, boat-only, etc.) can be added as additional
 * entries in TEMPLATES without any wizard rewrites.
 */

export const TEMPLATE_TYPES = Object.freeze({
  VEHICLE_DEFAULT: 'vehicle_default',
  SPECIALTY_ASSET: 'specialty_asset',
  // PHASE 1 (P0): Category-aware templates for non-car physical categories.
  // Each renders via the same SpecialtyInspectionWizard engine using the
  // canonical /prehire/submit and /posthire/submit endpoints. Submit body
  // structure is preserved: { template_type, specialty_data, exterior_photos }.
  MOTORCYCLE: 'motorcycle_default',
  TRAILER: 'trailer_default',
  BOAT: 'boat_default',
  JETSKI: 'jetski_default',
  EBIKE: 'ebike_default',
  ESCOOTER: 'escooter_default',
  MOBILITY_SCOOTER: 'mobility_scooter_default',
});

export const PHASES = Object.freeze({
  PREHIRE: 'prehire',
  POSTHIRE: 'posthire',
});

/**
 * Specialty asset pre-hire checklist template.
 * Each section is a group of prompts. Prompts can be:
 *   - { type: 'photos', key, label, min, max, required }
 *   - { type: 'checkbox', key, label, required }
 *   - { type: 'text', key, label, placeholder, required }
 *   - { type: 'radio', key, label, options: [{value, label}], required }
 */
const SPECIALTY_PREHIRE_SECTIONS = [
  {
    id: 'exterior_condition',
    title: 'Exterior Condition',
    description: 'Photograph every side and capture existing body, panel, signage or door condition.',
    prompts: [
      { type: 'photos', key: 'exterior_photos', label: 'Exterior photos (all sides)', min: 4, max: 12, required: true },
      { type: 'checkbox', key: 'signage_ok', label: 'Signage / branding intact at handover', required: false },
      { type: 'text', key: 'exterior_notes', label: 'Existing marks or damage (optional)', placeholder: 'e.g., scratch on rear panel near tail-light', required: false },
    ],
  },
  {
    id: 'tow_readiness',
    title: 'Trailer / Tow Readiness',
    description: 'Confirm the asset is safe to tow or move — skip if asset is not trailer-based.',
    prompts: [
      { type: 'photos', key: 'tow_photos', label: 'Coupling / hitch / chains / tyres photos', min: 0, max: 6, required: false },
      { type: 'checkbox', key: 'coupling_ok', label: 'Coupling / hitch secure', required: false },
      { type: 'checkbox', key: 'lights_ok', label: 'Trailer lights / plug tested and working', required: false },
      { type: 'checkbox', key: 'tyres_ok', label: 'Tyres / wheels in roadworthy condition', required: false },
    ],
  },
  {
    id: 'power_utility',
    title: 'Power / Utility Setup',
    description: 'Capture power inlets and generator state.',
    prompts: [
      { type: 'photos', key: 'power_photos', label: 'Power inlets / leads photos', min: 0, max: 6, required: false },
      { type: 'checkbox', key: 'power_inlet_ok', label: 'Power inlet(s) undamaged', required: false },
      { type: 'checkbox', key: 'lead_included', label: 'Power lead(s) included in hire', required: false },
      { type: 'checkbox', key: 'generator_included', label: 'Generator included in this hire', required: false },
      { type: 'text', key: 'power_notes', label: 'Power / generator notes', placeholder: 'e.g., 15A lead 10m, Honda EU30i included, refuelled', required: false },
    ],
  },
  {
    id: 'equipment_included',
    title: 'Equipment Included',
    description: 'Photograph every piece of equipment, stock or add-ons included at handover.',
    prompts: [
      { type: 'photos', key: 'equipment_photos', label: 'Equipment & included items', min: 2, max: 12, required: true },
      { type: 'text', key: 'equipment_checklist', label: 'Items handed over (free-text list)', placeholder: 'e.g., 2-group espresso machine, grinder, 2 urns, 1 cash float tray', required: false },
    ],
  },
  {
    id: 'cleanliness_handover',
    title: 'Cleanliness / Handover State',
    description: 'Confirm the asset is clean, ready for use and that missing items are flagged.',
    prompts: [
      { type: 'photos', key: 'interior_photos', label: 'Service area / interior photos', min: 2, max: 8, required: true },
      { type: 'radio', key: 'cleanliness', label: 'Asset cleanliness at handover',
        options: [
          { value: 'clean', label: 'Clean — ready for use' },
          { value: 'minor', label: 'Minor touch-up needed' },
          { value: 'noted', label: 'Noted pre-existing mess — photos attached' },
        ], required: true },
      { type: 'text', key: 'cleanliness_notes', label: 'Handover notes (optional)', required: false },
    ],
  },
  {
    id: 'specialty_notes',
    title: 'Specialty Notes / Handover Confirmation',
    description: 'Final host handover notes and renter acknowledgement.',
    prompts: [
      { type: 'text', key: 'host_notes', label: 'Host setup / access notes', placeholder: 'e.g., requires 2×15A circuits, 30min setup, packdown ~20min', required: false },
      { type: 'checkbox', key: 'renter_acknowledges_setup', label: 'Renter has read and acknowledges setup & rules', required: true },
      { type: 'checkbox', key: 'handover_photos_complete', label: 'Handover photo pack complete', required: true },
    ],
  },
];

const SPECIALTY_POSTHIRE_SECTIONS = [
  {
    id: 'exterior_return',
    title: 'Exterior Return Condition',
    description: 'Photograph every side after return. Flag any new dents, scratches, panel or signage damage.',
    prompts: [
      { type: 'photos', key: 'return_exterior_photos', label: 'Exterior return photos', min: 4, max: 12, required: true },
      { type: 'checkbox', key: 'new_exterior_damage', label: 'New exterior damage observed', required: false },
    ],
  },
  {
    id: 'tow_return',
    title: 'Trailer / Tow Return Condition',
    description: 'Skip if not trailer-based.',
    prompts: [
      { type: 'photos', key: 'return_tow_photos', label: 'Coupling / lights / wheels / frame photos', min: 0, max: 6, required: false },
      { type: 'checkbox', key: 'tow_safe_return', label: 'Tow hardware returned in safe condition', required: false },
    ],
  },
  {
    id: 'equipment_return',
    title: 'Equipment Return Check',
    description: 'Confirm all equipment / add-ons are returned and flag any damage or missing items.',
    prompts: [
      { type: 'photos', key: 'return_equipment_photos', label: 'Equipment return photos', min: 2, max: 12, required: true },
      { type: 'checkbox', key: 'all_equipment_returned', label: 'All listed equipment returned', required: true },
      { type: 'text', key: 'missing_items', label: 'Missing or damaged items (list if any)', required: false },
    ],
  },
  {
    id: 'cleanliness_packdown',
    title: 'Cleanliness / Packdown',
    description: 'Assess cleanliness and whether extra cleaning fees apply.',
    prompts: [
      { type: 'radio', key: 'return_cleanliness', label: 'Return cleanliness',
        options: [
          { value: 'clean', label: 'Clean — nothing required' },
          { value: 'minor', label: 'Minor — tidied by host at no charge' },
          { value: 'cleaning_required', label: 'Extra cleaning required' },
        ], required: true },
      { type: 'checkbox', key: 'waste_removed', label: 'Waste / rubbish removed', required: false },
      { type: 'text', key: 'cleaning_notes', label: 'Cleaning notes (optional)', required: false },
    ],
  },
  {
    id: 'damage_incident',
    title: 'Damage / Incident Notes',
    description: 'Log any new damage, missing parts, or setup-related issues.',
    prompts: [
      { type: 'checkbox', key: 'new_damage_observed', label: 'New damage observed on return', required: false },
      { type: 'text', key: 'damage_description', label: 'Damage description', placeholder: 'What, where, approximate severity', required: false },
      { type: 'photos', key: 'return_damage_photos', label: 'Damage photos', min: 0, max: 8, required: false },
    ],
  },
  {
    id: 'final_confirmation',
    title: 'Final Return Confirmation',
    description: 'Confirm the return is complete and whether anything requires admin review.',
    prompts: [
      { type: 'checkbox', key: 'return_complete', label: 'Return complete', required: true },
      { type: 'checkbox', key: 'evidence_pack_complete', label: 'Evidence pack complete', required: true },
      { type: 'checkbox', key: 'flag_for_review', label: 'Flag this hire for admin review', required: false },
    ],
  },
];

const TEMPLATES = {
  [TEMPLATE_TYPES.SPECIALTY_ASSET]: {
    [PHASES.PREHIRE]: {
      template_type: TEMPLATE_TYPES.SPECIALTY_ASSET,
      phase: PHASES.PREHIRE,
      title: 'Pre-Hire Inspection — Specialty Asset',
      sections: SPECIALTY_PREHIRE_SECTIONS,
    },
    [PHASES.POSTHIRE]: {
      template_type: TEMPLATE_TYPES.SPECIALTY_ASSET,
      phase: PHASES.POSTHIRE,
      title: 'Post-Hire Return — Specialty Asset',
      sections: SPECIALTY_POSTHIRE_SECTIONS,
    },
  },
};

// ============================================================================
// PHASE 1 (P0): Non-car category templates
// ----------------------------------------------------------------------------
// Each template re-uses the SpecialtyInspectionWizard engine — same evidence
// pipeline, same canonical /prehire/submit and /posthire/submit endpoints,
// same `specialty_data` payload field. Only the prompt definitions differ.
// Photos are required to live under `exterior_photos` (pre) / `return_exterior_photos`
// (post) so the existing canonical_lifecycle minimum-photo gate continues
// to work for non-default templates.
// ============================================================================

// ---------- MOTORCYCLE -------------------------------------------------------
const MOTORCYCLE_PREHIRE_SECTIONS = [
  {
    id: 'motorcycle_exterior',
    title: 'Bike Condition',
    description: 'Photograph all sides of the motorcycle plus tank, seat and any existing scratches.',
    prompts: [
      { type: 'photos', key: 'exterior_photos', label: 'Bike photos (all sides)', min: 4, max: 12, required: true },
      { type: 'text', key: 'existing_marks', label: 'Existing scratches / marks (optional)', required: false },
    ],
  },
  {
    id: 'motorcycle_odometer',
    title: 'Odometer Reading',
    description: 'Capture the current odometer reading clearly.',
    prompts: [
      { type: 'photos', key: 'odometer_photos', label: 'Odometer photo', min: 1, max: 2, required: true },
      { type: 'text', key: 'odometer_value', label: 'Odometer (km)', placeholder: 'e.g., 18450', required: true },
    ],
  },
  {
    id: 'motorcycle_fuel_tyres',
    title: 'Fuel & Running Gear',
    description: 'Confirm fuel level and visible condition of tyres, chain and brakes.',
    prompts: [
      { type: 'radio', key: 'fuel_level', label: 'Fuel level',
        options: [
          { value: 'empty', label: 'Empty' },
          { value: 'quarter', label: '¼' },
          { value: 'half', label: '½' },
          { value: 'three_quarters', label: '¾' },
          { value: 'full', label: 'Full' },
        ], required: true },
      { type: 'checkbox', key: 'tyres_ok', label: 'Tyres look roadworthy (no flat-spot, sufficient tread)', required: true },
      { type: 'checkbox', key: 'chain_ok', label: 'Chain tension and lubrication acceptable', required: false },
      { type: 'checkbox', key: 'brakes_ok', label: 'Brakes responsive on a brief test', required: true },
    ],
  },
  {
    id: 'motorcycle_safety',
    title: 'Helmet & Safety Gear',
    description: 'Confirm helmet condition and any provided safety gear.',
    prompts: [
      { type: 'photos', key: 'safety_gear_photos', label: 'Helmet / safety gear photos', min: 1, max: 6, required: true },
      { type: 'checkbox', key: 'helmet_provided', label: 'Helmet provided at handover', required: false },
      { type: 'checkbox', key: 'helmet_undamaged', label: 'Helmet free of visible damage', required: true },
    ],
  },
  {
    id: 'motorcycle_handover',
    title: 'Handover Confirmation',
    description: 'Final acknowledgement of keys handed over and rider acceptance.',
    prompts: [
      { type: 'text', key: 'keys_count', label: 'Keys handed over', placeholder: 'e.g., 1', required: true },
      { type: 'checkbox', key: 'rider_accepts', label: 'Rider accepts bike condition at handover', required: true },
    ],
  },
];

const MOTORCYCLE_POSTHIRE_SECTIONS = [
  {
    id: 'motorcycle_return_exterior',
    title: 'Return Bike Condition',
    description: 'Photograph all sides on return. Flag any new damage.',
    prompts: [
      { type: 'photos', key: 'return_exterior_photos', label: 'Return photos (all sides)', min: 4, max: 12, required: true },
      { type: 'checkbox', key: 'new_damage_observed', label: 'New damage observed', required: false },
      { type: 'text', key: 'damage_description', label: 'Damage description (if any)', required: false },
    ],
  },
  {
    id: 'motorcycle_return_odometer',
    title: 'Return Odometer',
    description: 'Capture the return odometer reading.',
    prompts: [
      { type: 'photos', key: 'return_odometer_photos', label: 'Odometer photo', min: 1, max: 2, required: true },
      { type: 'text', key: 'return_odometer_value', label: 'Return odometer (km)', required: true },
    ],
  },
  {
    id: 'motorcycle_return_fuel',
    title: 'Return Fuel Level',
    prompts: [
      { type: 'radio', key: 'return_fuel_level', label: 'Return fuel level',
        options: [
          { value: 'empty', label: 'Empty' },
          { value: 'quarter', label: '¼' },
          { value: 'half', label: '½' },
          { value: 'three_quarters', label: '¾' },
          { value: 'full', label: 'Full' },
        ], required: true },
    ],
  },
  {
    id: 'motorcycle_final',
    title: 'Final Return Confirmation',
    prompts: [
      { type: 'checkbox', key: 'helmet_returned', label: 'Helmet returned in good condition', required: false },
      { type: 'checkbox', key: 'keys_returned', label: 'Keys returned', required: true },
      { type: 'checkbox', key: 'return_complete', label: 'Return complete', required: true },
    ],
  },
];

// ---------- TRAILER ----------------------------------------------------------
const TRAILER_PREHIRE_SECTIONS = [
  {
    id: 'trailer_exterior',
    title: 'Trailer Condition',
    description: 'Photograph all sides plus any existing dents, scratches or rust.',
    prompts: [
      { type: 'photos', key: 'exterior_photos', label: 'Trailer photos (all sides)', min: 4, max: 12, required: true },
      { type: 'text', key: 'existing_marks', label: 'Existing marks / damage (optional)', required: false },
    ],
  },
  {
    id: 'trailer_coupling',
    title: 'Coupling, Safety Chains & Jockey Wheel',
    description: 'Confirm the trailer is safe to tow.',
    prompts: [
      { type: 'photos', key: 'coupling_photos', label: 'Coupling, chains & jockey-wheel photos', min: 2, max: 6, required: true },
      { type: 'checkbox', key: 'coupling_ok', label: 'Coupling secure and rated for the renter\'s tow vehicle', required: true },
      { type: 'checkbox', key: 'safety_chains_ok', label: 'Safety chains & D-shackles present and serviceable', required: true },
      { type: 'checkbox', key: 'jockey_wheel_ok', label: 'Jockey wheel functional', required: true },
    ],
  },
  {
    id: 'trailer_lights_brakes',
    title: 'Lights & Brakes',
    description: 'Test trailer plug, lights and (if fitted) override / electric brakes.',
    prompts: [
      { type: 'checkbox', key: 'lights_tested', label: 'Indicators / brake / tail-lights tested and working', required: true },
      { type: 'checkbox', key: 'brake_override_ok', label: 'Brake override / electric brake controller verified (if fitted)', required: false },
      { type: 'photos', key: 'lights_photos', label: 'Lights test photos (optional)', min: 0, max: 4, required: false },
    ],
  },
  {
    id: 'trailer_tyres',
    title: 'Tyres & Wheels',
    prompts: [
      { type: 'photos', key: 'tyre_photos', label: 'Tyre photos', min: 2, max: 6, required: true },
      { type: 'checkbox', key: 'tyres_ok', label: 'Tyres in roadworthy condition (no flat-spot, sufficient tread)', required: true },
      { type: 'checkbox', key: 'spare_present', label: 'Spare wheel present (if applicable)', required: false },
    ],
  },
  {
    id: 'trailer_load_deck',
    title: 'Load Deck / Interior',
    prompts: [
      { type: 'photos', key: 'load_deck_photos', label: 'Load deck / interior photos', min: 1, max: 6, required: false },
      { type: 'checkbox', key: 'deck_clean', label: 'Deck / interior clean and free of rubbish', required: false },
    ],
  },
  {
    id: 'trailer_handover',
    title: 'Handover Confirmation',
    prompts: [
      { type: 'checkbox', key: 'renter_owns_tow_vehicle_capacity', label: 'Renter confirms their tow vehicle has the correct towball rating and tow capacity', required: true },
      { type: 'checkbox', key: 'renter_accepts', label: 'Renter accepts trailer condition at handover', required: true },
    ],
  },
];

const TRAILER_POSTHIRE_SECTIONS = [
  {
    id: 'trailer_return_exterior',
    title: 'Return Trailer Condition',
    prompts: [
      { type: 'photos', key: 'return_exterior_photos', label: 'Return photos (all sides)', min: 4, max: 12, required: true },
      { type: 'checkbox', key: 'new_damage_observed', label: 'New damage observed', required: false },
      { type: 'text', key: 'damage_description', label: 'Damage description (if any)', required: false },
    ],
  },
  {
    id: 'trailer_return_coupling',
    title: 'Coupling & Lights Return Check',
    prompts: [
      { type: 'photos', key: 'return_coupling_photos', label: 'Coupling / chains photos', min: 1, max: 4, required: false },
      { type: 'checkbox', key: 'coupling_safe_return', label: 'Coupling, chains & jockey wheel returned in safe condition', required: true },
      { type: 'checkbox', key: 'lights_still_working', label: 'Lights still operational', required: true },
    ],
  },
  {
    id: 'trailer_return_tyres',
    title: 'Tyres & Wheels Return',
    prompts: [
      { type: 'photos', key: 'return_tyre_photos', label: 'Tyre photos', min: 1, max: 4, required: false },
      { type: 'checkbox', key: 'tyres_undamaged', label: 'Tyres returned undamaged', required: true },
    ],
  },
  {
    id: 'trailer_return_clean',
    title: 'Cleanliness',
    prompts: [
      { type: 'radio', key: 'return_cleanliness', label: 'Return cleanliness',
        options: [
          { value: 'clean', label: 'Clean — nothing required' },
          { value: 'minor', label: 'Minor — tidied at no charge' },
          { value: 'cleaning_required', label: 'Extra cleaning required' },
        ], required: true },
    ],
  },
  {
    id: 'trailer_return_final',
    title: 'Final Return Confirmation',
    prompts: [
      { type: 'checkbox', key: 'return_complete', label: 'Return complete', required: true },
    ],
  },
];

// ---------- BOAT -------------------------------------------------------------
const BOAT_PREHIRE_SECTIONS = [
  {
    id: 'boat_hull',
    title: 'Hull & Exterior',
    description: 'Photograph hull (port, starboard, bow, stern) and any existing dings.',
    prompts: [
      { type: 'photos', key: 'exterior_photos', label: 'Hull photos (all sides)', min: 4, max: 12, required: true },
      { type: 'text', key: 'existing_marks', label: 'Existing marks / dings (optional)', required: false },
    ],
  },
  {
    id: 'boat_engine',
    title: 'Engine & Hours',
    description: 'Capture the engine compartment and current engine-hours reading.',
    prompts: [
      { type: 'photos', key: 'engine_photos', label: 'Engine / engine-hours photos', min: 1, max: 4, required: true },
      { type: 'text', key: 'engine_hours_value', label: 'Engine hours reading', placeholder: 'e.g., 312.4', required: true },
      { type: 'checkbox', key: 'engine_starts_ok', label: 'Engine starts and idles cleanly', required: true },
    ],
  },
  {
    id: 'boat_fuel',
    title: 'Fuel Level',
    prompts: [
      { type: 'radio', key: 'fuel_level', label: 'Fuel level',
        options: [
          { value: 'empty', label: 'Empty' },
          { value: 'quarter', label: '¼' },
          { value: 'half', label: '½' },
          { value: 'three_quarters', label: '¾' },
          { value: 'full', label: 'Full' },
        ], required: true },
      { type: 'text', key: 'fuel_notes', label: 'Fuel type / notes (optional)', placeholder: 'e.g., 91 ULP, 70L tank', required: false },
    ],
  },
  {
    id: 'boat_safety',
    title: 'Safety Gear',
    description: 'Confirm life jackets, anchor, drain plug and any flares / EPIRB on board.',
    prompts: [
      { type: 'photos', key: 'safety_photos', label: 'Safety gear photos', min: 2, max: 8, required: true },
      { type: 'text', key: 'life_jackets_count', label: 'Life jackets on board (count)', required: true },
      { type: 'checkbox', key: 'anchor_present', label: 'Anchor & rope present', required: true },
      { type: 'checkbox', key: 'drain_plug_present', label: 'Drain plug present and bilge dry', required: true },
      { type: 'checkbox', key: 'drain_plug_ok', label: 'Drain plug fitted and secure before launch', required: true },
      { type: 'checkbox', key: 'bilge_pump_ok', label: 'Bilge pump checked and working, if fitted', required: true },
      { type: 'checkbox', key: 'flares_in_date', label: 'Flares / EPIRB in date (if required for waters of use)', required: false },
    ],
  },
  {
    id: 'boat_registration',
    title: 'Registration & HIN',
    description: 'Capture the Hull Identification Number / registration label for the legal record.',
    prompts: [
      { type: 'photos', key: 'hin_registration_photo', label: 'Photo of HIN / registration label', min: 1, max: 2, required: true },
    ],
  },
  {
    id: 'boat_trailer',
    title: 'Trailer (if applicable)',
    description: 'Skip if vessel is moored / not trailered.',
    prompts: [
      { type: 'photos', key: 'trailer_photos', label: 'Trailer photos', min: 0, max: 4, required: false },
      { type: 'checkbox', key: 'trailer_safe', label: 'Trailer coupling, chains and lights verified', required: false },
    ],
  },
  {
    id: 'boat_handover',
    title: 'Handover Confirmation',
    prompts: [
      { type: 'checkbox', key: 'skipper_holds_licence', label: 'Skipper holds a current boat / marine licence valid for these waters', required: true },
      { type: 'checkbox', key: 'renter_accepts', label: 'Renter accepts vessel condition at handover', required: true },
    ],
  },
];

const BOAT_POSTHIRE_SECTIONS = [
  {
    id: 'boat_return_hull',
    title: 'Return Hull Condition',
    prompts: [
      { type: 'photos', key: 'return_exterior_photos', label: 'Return hull photos (all sides)', min: 4, max: 12, required: true },
      { type: 'checkbox', key: 'new_damage_observed', label: 'New damage observed', required: false },
      { type: 'text', key: 'damage_description', label: 'Damage description (if any)', required: false },
    ],
  },
  {
    id: 'boat_return_engine',
    title: 'Return Engine & Hours',
    prompts: [
      { type: 'photos', key: 'return_engine_photos', label: 'Engine / hours photos', min: 1, max: 4, required: true },
      { type: 'text', key: 'return_engine_hours_value', label: 'Return engine hours', required: true },
    ],
  },
  {
    id: 'boat_return_fuel',
    title: 'Return Fuel Level',
    prompts: [
      { type: 'radio', key: 'return_fuel_level', label: 'Return fuel level',
        options: [
          { value: 'empty', label: 'Empty' },
          { value: 'quarter', label: '¼' },
          { value: 'half', label: '½' },
          { value: 'three_quarters', label: '¾' },
          { value: 'full', label: 'Full' },
        ], required: true },
    ],
  },
  {
    id: 'boat_return_safety',
    title: 'Safety Gear Returned',
    prompts: [
      { type: 'checkbox', key: 'all_life_jackets_returned', label: 'All life jackets returned', required: true },
      { type: 'checkbox', key: 'anchor_returned', label: 'Anchor & rope returned', required: true },
      { type: 'checkbox', key: 'drain_plug_returned', label: 'Drain plug returned', required: true },
      { type: 'checkbox', key: 'drain_plug_undamaged', label: 'Drain plug undamaged on return — no new water ingress evidence', required: true },
      { type: 'checkbox', key: 'bilge_area_clean', label: 'Bilge area returned clean — no unusual water accumulation (if applicable)', required: false },
    ],
  },
  {
    id: 'boat_return_final',
    title: 'Final Return Confirmation',
    prompts: [
      { type: 'checkbox', key: 'return_complete', label: 'Return complete', required: true },
    ],
  },
];

// ---------- JET SKI (PWC) ----------------------------------------------------
const JETSKI_PREHIRE_SECTIONS = [
  {
    id: 'jetski_hull',
    title: 'Hull & Exterior',
    prompts: [
      { type: 'photos', key: 'exterior_photos', label: 'Hull photos (all sides)', min: 4, max: 10, required: true },
      { type: 'text', key: 'existing_marks', label: 'Existing marks (optional)', required: false },
    ],
  },
  {
    id: 'jetski_engine',
    title: 'Engine & Hours',
    prompts: [
      { type: 'photos', key: 'engine_photos', label: 'Engine compartment / hours photo', min: 1, max: 3, required: true },
      { type: 'text', key: 'engine_hours_value', label: 'Engine hours', required: true },
    ],
  },
  {
    id: 'jetski_fuel',
    title: 'Fuel Level',
    prompts: [
      { type: 'radio', key: 'fuel_level', label: 'Fuel level',
        options: [
          { value: 'empty', label: 'Empty' },
          { value: 'quarter', label: '¼' },
          { value: 'half', label: '½' },
          { value: 'three_quarters', label: '¾' },
          { value: 'full', label: 'Full' },
        ], required: true },
    ],
  },
  {
    id: 'jetski_safety',
    title: 'PWC Safety Gear',
    description: 'Life jackets and lanyard / kill cord are mandatory.',
    prompts: [
      { type: 'photos', key: 'safety_photos', label: 'Safety gear photos', min: 1, max: 6, required: true },
      { type: 'text', key: 'life_jackets_count', label: 'Life jackets on board (count)', required: true },
      { type: 'checkbox', key: 'lanyard_present', label: 'Lanyard / kill-cord present and serviceable', required: true },
      { type: 'checkbox', key: 'rego_visible', label: 'Registration label legible', required: false },
    ],
  },
  {
    id: 'jetski_handover',
    title: 'Operator Acknowledgement',
    description: 'PWC licensing varies by state. Operator must hold the correct endorsement for the waters of use.',
    prompts: [
      { type: 'checkbox', key: 'operator_holds_pwc_licence', label: 'Operator confirms they hold the required PWC / marine licence for these waters', required: true },
      { type: 'checkbox', key: 'operator_briefed', label: 'Operator briefed on craft, throttle, kill-cord and zoning', required: true },
      { type: 'checkbox', key: 'renter_accepts', label: 'Renter accepts craft condition at handover', required: true },
    ],
  },
];

const JETSKI_POSTHIRE_SECTIONS = [
  {
    id: 'jetski_return_hull',
    title: 'Return Hull Condition',
    prompts: [
      { type: 'photos', key: 'return_exterior_photos', label: 'Return hull photos', min: 4, max: 10, required: true },
      { type: 'checkbox', key: 'new_damage_observed', label: 'New damage observed', required: false },
      { type: 'text', key: 'damage_description', label: 'Damage description', required: false },
    ],
  },
  {
    id: 'jetski_return_engine',
    title: 'Return Engine & Hours',
    prompts: [
      { type: 'photos', key: 'return_engine_photos', label: 'Engine / hours photo', min: 1, max: 3, required: true },
      { type: 'text', key: 'return_engine_hours_value', label: 'Return engine hours', required: true },
    ],
  },
  {
    id: 'jetski_return_fuel',
    title: 'Return Fuel Level',
    prompts: [
      { type: 'radio', key: 'return_fuel_level', label: 'Return fuel level',
        options: [
          { value: 'empty', label: 'Empty' },
          { value: 'quarter', label: '¼' },
          { value: 'half', label: '½' },
          { value: 'three_quarters', label: '¾' },
          { value: 'full', label: 'Full' },
        ], required: true },
    ],
  },
  {
    id: 'jetski_return_safety',
    title: 'Safety Gear Returned',
    prompts: [
      { type: 'checkbox', key: 'all_life_jackets_returned', label: 'All life jackets returned', required: true },
      { type: 'checkbox', key: 'lanyard_returned', label: 'Lanyard / kill-cord returned', required: true },
    ],
  },
  {
    id: 'jetski_return_final',
    title: 'Final Return Confirmation',
    prompts: [
      { type: 'checkbox', key: 'return_complete', label: 'Return complete', required: true },
    ],
  },
];

// ---------- eBIKE / E-SCOOTER (battery-based, no fuel/odometer) -------------
const buildBatteryPrehireSections = (label) => ([
  {
    id: 'battery_exterior',
    title: `${label} Condition`,
    prompts: [
      { type: 'photos', key: 'exterior_photos', label: `${label} photos (all sides)`, min: 4, max: 10, required: true },
      { type: 'text', key: 'existing_marks', label: 'Existing scuffs / damage (optional)', required: false },
    ],
  },
  {
    id: 'battery_charge',
    title: 'Battery & Charge',
    description: 'Capture the battery indicator at handover.',
    prompts: [
      { type: 'photos', key: 'battery_photos', label: 'Battery indicator photo', min: 1, max: 2, required: true },
      { type: 'radio', key: 'battery_level', label: 'Battery level at handover',
        options: [
          { value: '25', label: '≤ 25%' },
          { value: '50', label: '~ 50%' },
          { value: '75', label: '~ 75%' },
          { value: '100', label: 'Full' },
        ], required: true },
      { type: 'checkbox', key: 'charger_included', label: 'Charger included at handover', required: true },
    ],
  },
  {
    id: 'battery_brakes',
    title: 'Brakes & Tyres',
    prompts: [
      { type: 'checkbox', key: 'brakes_ok', label: 'Brakes responsive on a brief test', required: true },
      { type: 'checkbox', key: 'tyres_ok', label: 'Tyres / wheels in safe condition', required: true },
    ],
  },
  {
    id: 'battery_safety',
    title: 'Helmet & Safety',
    prompts: [
      { type: 'checkbox', key: 'helmet_provided', label: 'Helmet provided (if required by state law)', required: false },
      { type: 'checkbox', key: 'rider_acknowledges_state_rules', label: `Rider acknowledges local state rules for ${label.toLowerCase()} use`, required: true },
    ],
  },
  {
    id: 'battery_handover',
    title: 'Handover Confirmation',
    prompts: [
      { type: 'checkbox', key: 'renter_accepts', label: `Renter accepts ${label.toLowerCase()} condition at handover`, required: true },
    ],
  },
]);

const buildBatteryPosthireSections = (label) => ([
  {
    id: 'battery_return_exterior',
    title: `Return ${label} Condition`,
    prompts: [
      { type: 'photos', key: 'return_exterior_photos', label: `Return photos (all sides)`, min: 4, max: 10, required: true },
      { type: 'checkbox', key: 'new_damage_observed', label: 'New damage observed', required: false },
      { type: 'text', key: 'damage_description', label: 'Damage description (if any)', required: false },
    ],
  },
  {
    id: 'battery_return_charge',
    title: 'Return Battery Level',
    prompts: [
      { type: 'photos', key: 'return_battery_photos', label: 'Battery indicator photo', min: 1, max: 2, required: true },
      { type: 'radio', key: 'return_battery_level', label: 'Battery level on return',
        options: [
          { value: '25', label: '≤ 25%' },
          { value: '50', label: '~ 50%' },
          { value: '75', label: '~ 75%' },
          { value: '100', label: 'Full' },
        ], required: true },
      { type: 'checkbox', key: 'charger_returned', label: 'Charger returned', required: true },
    ],
  },
  {
    id: 'battery_return_final',
    title: 'Final Return Confirmation',
    prompts: [
      { type: 'checkbox', key: 'return_complete', label: 'Return complete', required: true },
    ],
  },
]);

const EBIKE_PREHIRE_SECTIONS = buildBatteryPrehireSections('eBike');
const EBIKE_POSTHIRE_SECTIONS = buildBatteryPosthireSections('eBike');
const ESCOOTER_PREHIRE_SECTIONS = buildBatteryPrehireSections('Electric scooter');
const ESCOOTER_POSTHIRE_SECTIONS = buildBatteryPosthireSections('Electric scooter');

// ---------- MOBILITY SCOOTER (dedicated accessibility-focused template) ------
// Distinct from eBike/electric_scooter: removes helmet prompts and cycling
// language; adds joystick/control accessibility, seat & armrest, anti-tip
// stability, and AU charger compatibility.
const MOBILITY_SCOOTER_PREHIRE_SECTIONS = [
  {
    id: 'mobility_condition',
    title: 'Mobility Scooter Condition',
    prompts: [
      { type: 'photos', key: 'exterior_photos', label: 'Mobility scooter photos (all sides)', min: 4, max: 10, required: true },
      { type: 'text', key: 'existing_marks', label: 'Existing scuffs / damage (optional)', required: false },
    ],
  },
  {
    id: 'mobility_safety',
    title: 'Operation & Safety',
    description: 'Confirm the scooter starts and basic safety systems work.',
    prompts: [
      { type: 'checkbox', key: 'powers_on_ok', label: 'Scooter powers on correctly', required: true },
      { type: 'checkbox', key: 'controls_ok', label: 'Joystick / tiller controls function smoothly', required: true },
      { type: 'checkbox', key: 'brakes_ok', label: 'Brakes engage when controls released', required: true },
      { type: 'checkbox', key: 'wheels_ok', label: 'Wheels / tyres in safe condition', required: true },
    ],
  },
  {
    id: 'mobility_accessibility',
    title: 'Accessibility',
    description: 'Confirm the scooter is suitable and adjusted for the renter.',
    prompts: [
      { type: 'checkbox', key: 'seat_ok', label: 'Seat condition is good and adjusted for renter', required: true },
      { type: 'checkbox', key: 'armrests_ok', label: 'Armrests secure (if fitted)', required: false },
      { type: 'checkbox', key: 'controls_accessible', label: 'Renter can comfortably reach and operate the controls', required: true },
    ],
  },
  {
    id: 'mobility_battery',
    title: 'Battery & Charging',
    description: 'Capture the battery indicator and confirm charger is provided.',
    prompts: [
      { type: 'photos', key: 'battery_photos', label: 'Battery indicator photo', min: 1, max: 2, required: true },
      { type: 'radio', key: 'battery_level', label: 'Battery level at handover',
        options: [
          { value: '25', label: '≤ 25%' },
          { value: '50', label: '~ 50%' },
          { value: '75', label: '~ 75%' },
          { value: '100', label: 'Full' },
        ], required: true },
      { type: 'checkbox', key: 'charger_included', label: 'Charger provided at handover', required: true },
      { type: 'checkbox', key: 'charger_au_compatible', label: 'Charger plug compatible with Australian power outlet', required: false },
    ],
  },
  {
    id: 'mobility_stability',
    title: 'Stability',
    prompts: [
      { type: 'checkbox', key: 'anti_tip_wheels_ok', label: 'Anti-tip wheels present and secure (if fitted)', required: false },
    ],
  },
  {
    id: 'mobility_handover',
    title: 'Handover Confirmation',
    prompts: [
      { type: 'checkbox', key: 'safe_operation_understood', label: 'Renter understands safe operation', required: true },
      { type: 'checkbox', key: 'renter_accepts', label: 'Renter accepts mobility scooter condition at handover', required: true },
    ],
  },
];

const MOBILITY_SCOOTER_POSTHIRE_SECTIONS = [
  {
    id: 'mobility_return_condition',
    title: 'Return Mobility Scooter Condition',
    prompts: [
      { type: 'photos', key: 'return_exterior_photos', label: 'Return photos (all sides)', min: 4, max: 10, required: true },
      { type: 'checkbox', key: 'new_damage_observed', label: 'New damage observed', required: false },
      { type: 'text', key: 'damage_description', label: 'Damage description (if any)', required: false },
    ],
  },
  {
    id: 'mobility_return_battery',
    title: 'Return Battery Level',
    prompts: [
      { type: 'photos', key: 'return_battery_photos', label: 'Battery indicator photo', min: 1, max: 2, required: true },
      { type: 'radio', key: 'return_battery_level', label: 'Battery level on return',
        options: [
          { value: '25', label: '≤ 25%' },
          { value: '50', label: '~ 50%' },
          { value: '75', label: '~ 75%' },
          { value: '100', label: 'Full' },
        ], required: true },
    ],
  },
  {
    id: 'mobility_return_accessories',
    title: 'Accessories Returned',
    prompts: [
      { type: 'checkbox', key: 'charger_returned', label: 'Charger returned', required: true },
      { type: 'text', key: 'other_accessories_returned', label: 'Other accessories returned (optional)', required: false },
    ],
  },
  {
    id: 'mobility_return_final',
    title: 'Final Return Confirmation',
    prompts: [
      { type: 'checkbox', key: 'return_complete', label: 'Return complete', required: true },
    ],
  },
];

// Register all new templates
TEMPLATES[TEMPLATE_TYPES.MOTORCYCLE] = {
  [PHASES.PREHIRE]: { template_type: TEMPLATE_TYPES.MOTORCYCLE, phase: PHASES.PREHIRE, title: 'Pre-Hire Inspection — Motorcycle', sections: MOTORCYCLE_PREHIRE_SECTIONS },
  [PHASES.POSTHIRE]: { template_type: TEMPLATE_TYPES.MOTORCYCLE, phase: PHASES.POSTHIRE, title: 'Return Inspection — Motorcycle', sections: MOTORCYCLE_POSTHIRE_SECTIONS },
};
TEMPLATES[TEMPLATE_TYPES.TRAILER] = {
  [PHASES.PREHIRE]: { template_type: TEMPLATE_TYPES.TRAILER, phase: PHASES.PREHIRE, title: 'Pre-Hire Inspection — Trailer', sections: TRAILER_PREHIRE_SECTIONS },
  [PHASES.POSTHIRE]: { template_type: TEMPLATE_TYPES.TRAILER, phase: PHASES.POSTHIRE, title: 'Return Inspection — Trailer', sections: TRAILER_POSTHIRE_SECTIONS },
};
TEMPLATES[TEMPLATE_TYPES.BOAT] = {
  [PHASES.PREHIRE]: { template_type: TEMPLATE_TYPES.BOAT, phase: PHASES.PREHIRE, title: 'Pre-Hire Inspection — Boat', sections: BOAT_PREHIRE_SECTIONS },
  [PHASES.POSTHIRE]: { template_type: TEMPLATE_TYPES.BOAT, phase: PHASES.POSTHIRE, title: 'Return Inspection — Boat', sections: BOAT_POSTHIRE_SECTIONS },
};
TEMPLATES[TEMPLATE_TYPES.JETSKI] = {
  [PHASES.PREHIRE]: { template_type: TEMPLATE_TYPES.JETSKI, phase: PHASES.PREHIRE, title: 'Pre-Hire Inspection — Jet Ski', sections: JETSKI_PREHIRE_SECTIONS },
  [PHASES.POSTHIRE]: { template_type: TEMPLATE_TYPES.JETSKI, phase: PHASES.POSTHIRE, title: 'Return Inspection — Jet Ski', sections: JETSKI_POSTHIRE_SECTIONS },
};
TEMPLATES[TEMPLATE_TYPES.EBIKE] = {
  [PHASES.PREHIRE]: { template_type: TEMPLATE_TYPES.EBIKE, phase: PHASES.PREHIRE, title: 'Pre-Hire Inspection — eBike', sections: EBIKE_PREHIRE_SECTIONS },
  [PHASES.POSTHIRE]: { template_type: TEMPLATE_TYPES.EBIKE, phase: PHASES.POSTHIRE, title: 'Return Inspection — eBike', sections: EBIKE_POSTHIRE_SECTIONS },
};
TEMPLATES[TEMPLATE_TYPES.ESCOOTER] = {
  [PHASES.PREHIRE]: { template_type: TEMPLATE_TYPES.ESCOOTER, phase: PHASES.PREHIRE, title: 'Pre-Hire Inspection — Electric Scooter', sections: ESCOOTER_PREHIRE_SECTIONS },
  [PHASES.POSTHIRE]: { template_type: TEMPLATE_TYPES.ESCOOTER, phase: PHASES.POSTHIRE, title: 'Return Inspection — Electric Scooter', sections: ESCOOTER_POSTHIRE_SECTIONS },
};
TEMPLATES[TEMPLATE_TYPES.MOBILITY_SCOOTER] = {
  [PHASES.PREHIRE]: { template_type: TEMPLATE_TYPES.MOBILITY_SCOOTER, phase: PHASES.PREHIRE, title: 'Pre-Hire Inspection — Mobility Scooter', sections: MOBILITY_SCOOTER_PREHIRE_SECTIONS },
  [PHASES.POSTHIRE]: { template_type: TEMPLATE_TYPES.MOBILITY_SCOOTER, phase: PHASES.POSTHIRE, title: 'Return Inspection — Mobility Scooter', sections: MOBILITY_SCOOTER_POSTHIRE_SECTIONS },
};

/**
 * Map a vehicle_category value to a template_type.
 * `car` is the only category that uses VEHICLE_DEFAULT (the existing canonical
 * vehicle wizard). Every other category resolves to its own template and
 * mounts the SpecialtyInspectionWizard engine with its prompt set.
 */
export function templateTypeForCategory(category) {
  if (category === 'specialty_asset') return TEMPLATE_TYPES.SPECIALTY_ASSET;
  if (category === 'motorcycle') return TEMPLATE_TYPES.MOTORCYCLE;
  if (category === 'trailer') return TEMPLATE_TYPES.TRAILER;
  if (category === 'boat') return TEMPLATE_TYPES.BOAT;
  if (category === 'jetski') return TEMPLATE_TYPES.JETSKI;
  if (category === 'ebike') return TEMPLATE_TYPES.EBIKE;
  if (category === 'electric_scooter') return TEMPLATE_TYPES.ESCOOTER;
  if (category === 'mobility_scooter') return TEMPLATE_TYPES.MOBILITY_SCOOTER;
  return TEMPLATE_TYPES.VEHICLE_DEFAULT;
}

/**
 * Primary resolver. Returns:
 *   { template_type: 'vehicle_default' }                  when the existing vehicle wizard should run
 *   { template_type, phase, title, sections }             for any non-default template (e.g. specialty_asset)
 */
export function getChecklistTemplateForCategory(category, phase) {
  const tt = templateTypeForCategory(category);
  if (tt === TEMPLATE_TYPES.VEHICLE_DEFAULT) {
    return { template_type: TEMPLATE_TYPES.VEHICLE_DEFAULT };
  }
  return TEMPLATES[tt]?.[phase] || { template_type: TEMPLATE_TYPES.VEHICLE_DEFAULT };
}

/**
 * Convenience for components that only care about "should I render the
 * specialty wizard instead of the default vehicle wizard?".
 */
export function shouldUseSpecialtyTemplate(category) {
  return templateTypeForCategory(category) === TEMPLATE_TYPES.SPECIALTY_ASSET;
}

/**
 * PHASE 1 (P0): True for any category that mounts the SpecialtyInspectionWizard
 * engine instead of the canonical vehicle wizard. Includes the original
 * `specialty_asset` and the seven new physical categories.
 */
export function usesNonDefaultTemplate(category) {
  return templateTypeForCategory(category) !== TEMPLATE_TYPES.VEHICLE_DEFAULT;
}
